import { z } from "zod";

export const healthResponseSchema = z.object({
  ok: z.literal(true),
  status: z.literal("ok"),
  service: z.string(),
  timestamp: z.string(),
});

export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const sourceSchema = z.enum(["api", "manual"]);
export type Source = z.infer<typeof sourceSchema>;

export const syncStatusSchema = z.enum(["success", "failed"]);
export type SyncStatus = z.infer<typeof syncStatusSchema>;

const isoTimestamp = z
  .string()
  .refine((s) => !Number.isNaN(Date.parse(s)), "expected ISO 8601 timestamp");

const optionalIsoTimestamp = isoTimestamp.optional().nullable();

const nonNegative = z.number().finite().nonnegative();
const positive = z.number().finite().positive();
const money = z.number().finite();

const baseRecord = {
  id: z.string().uuid(),
  source: sourceSchema,
  external_account_id: z.string().nullable().optional(),
  last_updated_at: isoTimestamp,
  // Optional + nullable so the schema parses even before the 0002 migration
  // is applied to a given environment (column absent → undefined; column
  // present but unset → null).
  last_synced_at: isoTimestamp.nullable().optional(),
  last_sync_status: syncStatusSchema.nullable().optional(),
  created_at: isoTimestamp,
};

export const bankAccountTypeSchema = z.enum(["checking", "savings", "cd"]);
export type BankAccountType = z.infer<typeof bankAccountTypeSchema>;

export const bankAccountSchema = z.object({
  ...baseRecord,
  name: z.string().min(1),
  type: bankAccountTypeSchema,
  institution: z.string().min(1),
  current_balance: money,
  interest_rate: z.number().finite().nullable().optional(),
  cd_maturity_date: optionalIsoTimestamp,
  // Last 4 of the account number, captured from Plaid `accounts.mask` at
  // sync time (FIN-41). Optional/nullable so the schema parses before
  // migration 0012 is applied and on manual rows that never carry one.
  mask: z.string().nullable().optional(),
});
export type BankAccount = z.infer<typeof bankAccountSchema>;

export const bankAccountCreateSchema = z.object({
  name: z.string().min(1),
  type: bankAccountTypeSchema,
  institution: z.string().min(1),
  current_balance: money,
  interest_rate: z.number().finite().nullable().optional(),
  cd_maturity_date: optionalIsoTimestamp,
  source: sourceSchema.optional(),
  external_account_id: z.string().nullable().optional(),
});
export type BankAccountCreate = z.infer<typeof bankAccountCreateSchema>;

export const bankAccountUpdateSchema = bankAccountCreateSchema.partial();
export type BankAccountUpdate = z.infer<typeof bankAccountUpdateSchema>;

export const creditCardSchema = z.object({
  ...baseRecord,
  name: z.string().min(1),
  issuer: z.string().min(1),
  current_balance_owed: nonNegative,
  credit_limit: positive,
  available_credit: nonNegative,
  utilization_pct: nonNegative,
  due_date: optionalIsoTimestamp,
  minimum_payment: z.number().finite().nullable().optional(),
  // Last 4 of the account number — see `bankAccountSchema.mask`. FIN-41.
  mask: z.string().nullable().optional(),
});
export type CreditCard = z.infer<typeof creditCardSchema>;

export const creditCardCreateSchema = z.object({
  name: z.string().min(1),
  issuer: z.string().min(1),
  current_balance_owed: nonNegative,
  credit_limit: positive,
  due_date: optionalIsoTimestamp,
  minimum_payment: z.number().finite().nullable().optional(),
  source: sourceSchema.optional(),
  external_account_id: z.string().nullable().optional(),
});
export type CreditCardCreate = z.infer<typeof creditCardCreateSchema>;

export const creditCardUpdateSchema = creditCardCreateSchema.partial();
export type CreditCardUpdate = z.infer<typeof creditCardUpdateSchema>;

export const investmentSchema = z.object({
  ...baseRecord,
  name: z.string().min(1),
  ticker: z.string().nullable().optional(),
  quantity: nonNegative,
  cost_basis: nonNegative,
  current_price: nonNegative,
  current_value: nonNegative,
  pnl_amount: money,
  pnl_pct: money,
  // Plaid `security_id` for api-sourced holdings (FIN-34). Optional +
  // nullable so the schema parses before migration 0010 is applied and
  // for manual rows where the column is unset.
  plaid_security_id: z.string().nullable().optional(),
});
export type Investment = z.infer<typeof investmentSchema>;

export const investmentCreateSchema = z.object({
  name: z.string().min(1),
  ticker: z.string().nullable().optional(),
  quantity: nonNegative,
  cost_basis: nonNegative,
  current_price: nonNegative.optional(),
  source: sourceSchema.optional(),
  external_account_id: z.string().nullable().optional(),
});
export type InvestmentCreate = z.infer<typeof investmentCreateSchema>;

export const investmentUpdateSchema = investmentCreateSchema.partial();
export type InvestmentUpdate = z.infer<typeof investmentUpdateSchema>;

export const plaidItemSchema = z.object({
  id: z.string().uuid(),
  item_id: z.string(),
  institution_name: z.string(),
  status: z.string(),
  // Optional + nullable so the schema parses before migration 0003 is applied.
  last_synced_at: isoTimestamp.nullable().optional(),
  // Plaid account_ids the user selected during Link's Account Select flow
  // (FIN-31). null = no whitelist (sync everything Plaid returns).
  selected_account_ids: z.array(z.string()).nullable().optional(),
  // Cursor for Plaid `/transactions/sync` (FIN-33). null = never synced.
  // Optional so the schema parses on deployments pre-dating migration 0009.
  transactions_cursor: z.string().nullable().optional(),
  // Set once the background "initial transactions sync" scheduled by
  // `POST /plaid/exchange` finishes successfully (FIN-40). null means the
  // auto-sync is still in flight or has not yet succeeded — the UI uses
  // this to distinguish "still pulling" from "synced, no transactions".
  initial_transactions_synced_at: isoTimestamp.nullable().optional(),
  // Last transactions-sync error message (auto or manual), cleared on the
  // next successful sync (FIN-40). Surfaced so the UI can prompt retry.
  last_sync_error: z.string().nullable().optional(),
  created_at: isoTimestamp,
});
export type PlaidItem = z.infer<typeof plaidItemSchema>;

export const plaidEnvironmentSchema = z.enum([
  "sandbox",
  "development",
  "production",
]);
export type PlaidEnvironment = z.infer<typeof plaidEnvironmentSchema>;

export const plaidSecretPresenceSchema = z.object({
  PLAID_CLIENT_ID: z.boolean(),
  PLAID_SECRET: z.boolean(),
  PLAID_ENCRYPTION_KEY: z.boolean(),
});
export type PlaidSecretPresence = z.infer<typeof plaidSecretPresenceSchema>;

export const plaidStatusSchema = z.object({
  configured: z.boolean(),
  environment: plaidEnvironmentSchema.nullable(),
  // Optional for forward-compat with deployments that pre-date FIN-13.
  secrets: plaidSecretPresenceSchema.optional(),
});
export type PlaidStatus = z.infer<typeof plaidStatusSchema>;

export const plaidLinkTokenResponseSchema = z.object({
  link_token: z.string().min(1),
  expiration: z.string(),
});
export type PlaidLinkTokenResponse = z.infer<typeof plaidLinkTokenResponseSchema>;

// Shape of one entry in Plaid Link's `onSuccess` metadata `accounts` array.
// Only `id` (the Plaid account_id) is load-bearing here — the rest is kept
// optional so the front-end can forward the metadata verbatim without us
// having to validate every field.
export const plaidLinkAccountSchema = z.object({
  id: z.string().min(1),
  name: z.string().optional(),
  mask: z.string().nullable().optional(),
  type: z.string().optional(),
  subtype: z.string().nullable().optional(),
});
export type PlaidLinkAccount = z.infer<typeof plaidLinkAccountSchema>;

export const plaidExchangeRequestSchema = z.object({
  public_token: z.string().min(1),
  institution_name: z.string().min(1),
  // Accounts the user selected in Link's Account Select UI (FIN-31).
  // Optional for back-compat with clients that pre-date the customization
  // and for fallback when PLAID_LINK_CUSTOMIZATION_NAME is unset on the
  // Worker. When absent, the server stores no whitelist and sync syncs all
  // accounts Plaid returns.
  accounts: z.array(plaidLinkAccountSchema).optional(),
});
export type PlaidExchangeRequest = z.infer<typeof plaidExchangeRequestSchema>;

export const plaidSyncErrorSchema = z.object({
  account_id: z.string(),
  message: z.string(),
});
export type PlaidSyncError = z.infer<typeof plaidSyncErrorSchema>;

// Result of `POST /plaid/sync/:item_id` (FIN-30). `synced` counts mapped
// upserts (insert + update both count); `skipped` counts accounts whose
// Plaid type is outside the MVP mapping (e.g. investments, loans). `errors`
// reports per-account failures that didn't fail the whole sync.
export const plaidSyncResultSchema = z.object({
  synced: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  errors: z.array(plaidSyncErrorSchema),
  item: plaidItemSchema,
});
export type PlaidSyncResult = z.infer<typeof plaidSyncResultSchema>;

// Result of `POST /plaid/investments/sync/:item_id` (FIN-34). Mirrors the
// FIN-30 balance sync result shape (counts + per-row errors + the item),
// where each "row" is one Plaid holding upserted into `investments`.
// `skipped` covers: holdings for non-whitelisted accounts (FIN-31), out-
// of-scope security types (e.g. derivatives), and holdings Plaid returned
// without a matching entry in `securities[]`.
export const plaidInvestmentsSyncResultSchema = z.object({
  synced: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  errors: z.array(plaidSyncErrorSchema),
  item: plaidItemSchema,
});
export type PlaidInvestmentsSyncResult = z.infer<
  typeof plaidInvestmentsSyncResultSchema
>;

export const transactionSchema = z.object({
  id: z.string().uuid(),
  account_id: z.string(),
  plaid_transaction_id: z.string(),
  amount: money,
  currency: z.string().nullable().optional(),
  date: z.string(),
  name: z.string(),
  merchant_name: z.string().nullable().optional(),
  // Legacy Plaid `category` array, collapsed to a `"A > B > C"` path
  // (FIN-33). Kept for compatibility — Plaid is deprecating it in favor
  // of `personal_finance_category_*` below.
  category: z.string().nullable().optional(),
  // FIN-48: Plaid's newer `personal_finance_category` taxonomy. Optional
  // + nullable so the schema parses before migration 0013 is applied and
  // for rows synced before the upsert started persisting it.
  personal_finance_category_primary: z.string().nullable().optional(),
  personal_finance_category_detailed: z.string().nullable().optional(),
  personal_finance_category_confidence: z.string().nullable().optional(),
  pending: z.boolean(),
  created_at: isoTimestamp,
  updated_at: isoTimestamp,
});
export type Transaction = z.infer<typeof transactionSchema>;

// Result of `POST /plaid/transactions/sync/:item_id` (FIN-33). Counts the
// rows applied to the local `transactions` table after draining every page
// Plaid returned. `skipped` counts transactions whose Plaid `account_id`
// fell outside the item's `selected_account_ids` whitelist.
export const plaidTransactionsSyncResultSchema = z.object({
  added: z.number().int().nonnegative(),
  modified: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  item: plaidItemSchema,
});
export type PlaidTransactionsSyncResult = z.infer<
  typeof plaidTransactionsSyncResultSchema
>;

// Surfaced in the delete-confirmation dialog so the user sees what the
// cascade will remove (FIN-36). `accounts_count` is the union of api-sourced
// rows across `bank_accounts`, `credit_cards`, and `investments` belonging to
// the item; `transactions_count` is the row count in `transactions` whose
// `account_id` matches one of those Plaid account_ids.
export const plaidItemSummarySchema = z.object({
  item_id: z.string(),
  institution_name: z.string(),
  accounts_count: z.number().int().nonnegative(),
  transactions_count: z.number().int().nonnegative(),
});
export type PlaidItemSummary = z.infer<typeof plaidItemSummarySchema>;

// Result of `DELETE /plaid/items/:item_id` (FIN-36). Counts the rows actually
// removed by the cascade so the UI can confirm what happened. `plaid_remove_ok`
// reflects whether the upstream `/item/remove` call to Plaid succeeded; a
// `false` here does not block the local deletion per the issue's best-effort
// requirement.
export const plaidItemDeleteResultSchema = z.object({
  item_id: z.string(),
  accounts_deleted: z.number().int().nonnegative(),
  transactions_deleted: z.number().int().nonnegative(),
  plaid_remove_ok: z.boolean(),
});
export type PlaidItemDeleteResult = z.infer<typeof plaidItemDeleteResultSchema>;

export const summaryBreakdownSchema = z.object({
  bank_accounts: nonNegative,
  investments: nonNegative,
  credit_cards_owed: nonNegative,
});
export type SummaryBreakdown = z.infer<typeof summaryBreakdownSchema>;

export const summarySchema = z.object({
  total_assets: nonNegative,
  total_liabilities: nonNegative,
  net_worth: money,
  cash_balance: nonNegative,
  credit_card_owed: nonNegative,
  investments_total: nonNegative,
  last_updated_at: z.string().nullable(),
  breakdown_by_category: summaryBreakdownSchema,
});
export type Summary = z.infer<typeof summarySchema>;

export const errorResponseSchema = z.object({
  error: z.string(),
  details: z.unknown().optional(),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

// Compact account header shape returned by `GET /accounts/:accountId/transactions`
// (FIN-41). `kind` tells the UI whether to treat `current_balance` as an
// asset or a liability for amount-direction display. `plaid_item` is set
// only when `source === "api"` and the account belongs to a Plaid item;
// it carries the bits the page needs to drive the "Sync now" button and
// the post-Link polling spinner.
export const accountSummarySchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(["bank", "credit"]),
  name: z.string(),
  institution: z.string(),
  mask: z.string().nullable(),
  current_balance: money,
  source: sourceSchema,
  external_account_id: z.string().nullable(),
  last_synced_at: isoTimestamp.nullable(),
  last_sync_status: syncStatusSchema.nullable(),
  plaid_item: z
    .object({
      item_id: z.string(),
      institution_name: z.string(),
      last_synced_at: isoTimestamp.nullable(),
      initial_transactions_synced_at: isoTimestamp.nullable(),
      last_sync_error: z.string().nullable(),
    })
    .nullable(),
});
export type AccountSummary = z.infer<typeof accountSummarySchema>;

// Direction filter for `GET /accounts/:accountId/transactions` (FIN-43).
// Mirrors the user-facing Income / Spending toggle on the transactions page.
// Sign convention matches FIN-41: a Plaid `amount < 0` is an inflow
// (income) and `amount > 0` is an outflow (spending). Zero-amount rows are
// excluded from both buckets.
export const transactionDirectionSchema = z.enum(["income", "spending"]);
export type TransactionDirection = z.infer<typeof transactionDirectionSchema>;

// Per-direction totals across the full transactions table for the account,
// not just the current page. Both totals are user-facing positive numbers
// (absolute values of the displayed amounts). The mini badge inside each
// filter pill renders these unchanged — the badge's own color carries the
// sign convention.
export const accountTransactionsSummarySchema = z.object({
  income_total: nonNegative,
  spending_total: nonNegative,
  currency: z.string(),
});
export type AccountTransactionsSummary = z.infer<
  typeof accountTransactionsSummarySchema
>;

export const accountTransactionsResponseSchema = z.object({
  account: accountSummarySchema,
  transactions: z.array(transactionSchema),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  has_more: z.boolean(),
  summary: accountTransactionsSummarySchema,
});
export type AccountTransactionsResponse = z.infer<
  typeof accountTransactionsResponseSchema
>;

// Top-nav global search (FIN-47): one round trip returns grouped matches
// across accounts and transactions. Each list is independently capped by
// the server (see SEARCH_ACCOUNTS_LIMIT / SEARCH_TRANSACTIONS_LIMIT on the
// api). `account_id` on the transaction row is the parent account's
// internal UUID (bank_accounts.id or credit_cards.id), already resolved
// from the Plaid account_id so the UI can route to /accounts/:id/transactions
// without a second lookup. `amount` is Plaid's raw value; the UI inverts
// the sign for display the same way the per-account page does (FIN-41).
export const searchAccountResultSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(["bank", "credit"]),
  name: z.string(),
  institution: z.string(),
  mask: z.string().nullable(),
  balance: money,
  currency: z.string(),
});
export type SearchAccountResult = z.infer<typeof searchAccountResultSchema>;

export const searchTransactionResultSchema = z.object({
  id: z.string().uuid(),
  account_id: z.string().uuid(),
  name: z.string(),
  merchant_name: z.string().nullable(),
  amount: money,
  currency: z.string().nullable(),
  date: z.string(),
});
export type SearchTransactionResult = z.infer<
  typeof searchTransactionResultSchema
>;

export const searchResponseSchema = z.object({
  query: z.string(),
  accounts: z.array(searchAccountResultSchema),
  transactions: z.array(searchTransactionResultSchema),
});
export type SearchResponse = z.infer<typeof searchResponseSchema>;

